import * as pmath from './pmath.js';
import { mulberry32, range } from './rng.js';
import { BoxColliderField } from './worldgen/boxColliders.js';
import { raceShutterPose } from './raceObstacles.js';
import type { RaceBox, RaceCourse, RaceVec3 } from './raceCourse.js';
import {raceBoxCollider,raceBoxLocal} from './raceGeometry.js';
import {raceArenaWalls} from './raceEncounter.js';

export const RACE_TARGET_REFUND = 25;
export const RACE_TARGET_RESPAWN_SECONDS = 8;
export const RACE_TARGET_RANGE = 180;

/** Non-solid floating robot. Time is seconds on the shared round clock. */
export interface RaceTarget {
  id: string;
  kind?: 'percussion'|'emitter'|'core';
  beatOffset?:number;
  /** The launch deck from which the generator checks a clear shot. */
  sectionId: string;
  center: RaceVec3;
  right: RaceVec3;
  travel: number;
  bob: number;
  period: number;
  phase: number;
  radius: number;
  /** The reachability ledger credits one hit here, never repeated respawns. */
  plannedRefill: boolean;
}
export interface RaceTargetHit { targetId: string; point: RaceVec3; distance: number; time?:number }

export function sampleRaceTarget(target: RaceTarget, timeSeconds: number): RaceVec3 {
  const t = Number.isFinite(timeSeconds) ? timeSeconds : 0;
  const angle = pmath.TWO_PI * (((t + target.phase) % target.period + target.period) % target.period) / target.period;
  const offset = target.travel * pmath.sin(angle);
  return [target.center[0] + target.right[0] * offset, target.center[1] + target.bob * pmath.sin(angle * 2), target.center[2] + target.right[2] * offset];
}

export function refundRaceTargetEnergy(energy: number, maximum = 100): number {
  return Math.min(maximum, Math.max(0, energy) + RACE_TARGET_REFUND);
}

const staticFields = new WeakMap<object, { boxes: RaceBox[]; field: BoxColliderField }>();
function staticField(course: Pick<RaceCourse, 'boxes' | 'radius'>): BoxColliderField {
  const cached = staticFields.get(course);
  if (cached?.boxes === course.boxes) return cached.field;
  const field = new BoxColliderField(course.radius);
  for (const box of course.boxes) field.addBox(raceBoxCollider(box,course.radius));
  staticFields.set(course, { boxes: course.boxes, field });
  return field;
}

function segmentBox(box: RaceBox, from: readonly number[], to: readonly number[]): number | null {
  const a = raceBoxLocal(box,from), b = raceBoxLocal(box,to);
  let enter = 0, leave = 1;
  for (let axis = 0; axis < 3; axis++) {
    const d = b[axis] - a[axis], half = box.size[axis] / 2;
    if (Math.abs(d) < 1e-9) { if (Math.abs(a[axis]) > half) return null; }
    else { const near = (-half - a[axis]) / d, far = (half - a[axis]) / d; enter = Math.max(enter, Math.min(near, far)); leave = Math.min(leave, Math.max(near, far)); if (enter > leave) return null; }
  }
  return enter;
}

/** Nearest available robot hit, with the same static and moving solids as play. */
export function raycastRaceTarget(course: Pick<RaceCourse, 'targets' | 'boxes' | 'radius' | 'shutters'|'arena'|'rhythm'>, origin: readonly number[], direction: readonly number[], timeSeconds: number, availableIds?: ReadonlySet<string>, maxDistance = RACE_TARGET_RANGE): RaceTargetHit | null {
  if (origin.length !== 3 || direction.length !== 3 || !origin.every(Number.isFinite) || !direction.every(Number.isFinite) || !Number.isFinite(timeSeconds) || !Number.isFinite(maxDistance) || maxDistance <= 0) return null;
  const length = pmath.hypot(direction[0], direction[1], direction[2]);
  if (length < 1e-9) return null;
  const d: RaceVec3 = [direction[0] / length, direction[1] / length, direction[2] / length];
  const field = staticField(course);
  const panels = course.shutters.flatMap(shutter => raceShutterPose(shutter, timeSeconds).panels);
  panels.push(...raceArenaWalls(course,timeSeconds));
  let nearest: RaceTargetHit | null = null;
  for (const target of course.targets) {
    if (availableIds && !availableIds.has(target.id)) continue;
    const center = sampleRaceTarget(target, timeSeconds);
    const x = origin[0] - center[0], y = origin[1] - center[1], z = origin[2] - center[2];
    const projection = x * d[0] + y * d[1] + z * d[2];
    const discriminant = projection * projection - (x * x + y * y + z * z - target.radius * target.radius);
    if (discriminant < 0) continue;
    const root = Math.sqrt(discriminant), distance = Math.max(0, -projection - root);
    if (-projection + root < 0 || distance > maxDistance || nearest && distance >= nearest.distance) continue;
    const point: RaceVec3 = [origin[0] + d[0] * distance, origin[1] + d[1] * distance, origin[2] + d[2] * distance];
    if (field.segmentHit(origin[0], origin[1] + course.radius, origin[2], point[0], point[1] + course.radius, point[2]) >= 0 || panels.some(panel => segmentBox(panel, origin, point) !== null)) continue;
    nearest = { targetId: target.id, point, distance };
  }
  return nearest;
}

const quantize = (value: number) => Math.round(value * 100) / 100;
function pointBoxClearance(box: RaceBox, point: RaceVec3): number {
  const local=raceBoxLocal(box,point);
  return pmath.hypot(...local.map((v,i)=>Math.max(0,Math.abs(v)-box.size[i]/2)) as RaceVec3);
}

/** Place bounded motion away from solids/routes; prove an unobstructed deck shot. */
export function generateRaceTargets(course: Pick<RaceCourse, 'seed' | 'sections' | 'boxes' | 'radius' | 'shutters'>, plannedSectionIds: ReadonlySet<string>): RaceTarget[] {
  const rng = mulberry32(course.seed ^ 0xa715d39b), targets: RaceTarget[] = [];
  for (const [index, section] of course.sections.entries()) {
    const deck = course.boxes.find(box => box.id === (index === 0 ? 'start' : `${course.sections[index - 1].id}-landing`))!;
    const eye: RaceVec3 = [deck.center[0], deck.center[1] + deck.size[1] / 2 + 1.45, deck.center[2]];
    // The opening shot is visible from every grid slot without leaving the deck.
    const eyes: RaceVec3[] = index === 0 ? Array.from({length:8}, (_, slot) => {
      const lateral=(slot%4-1.5)*4, behind=3+Math.floor(slot/4)*4;
      return [eye[0]+section.frame.right[0]*lateral-section.frame.forward[0]*behind,
        eye[1],eye[2]+section.frame.right[2]*lateral-section.frame.forward[2]*behind];
    }) : [eye];
    for (let attempt = 0; attempt < 48; attempt++) {
      const lateral = range(rng, 15, index === 0 ? 18 : 24) * (rng() < .5 ? -1 : 1), along = index === 0 ? range(rng, 20, 26) : range(rng, -6, 8);
      const target: RaceTarget = { id: `target-${section.id}`, sectionId: section.id,
        center: [quantize(eye[0] + section.frame.right[0] * lateral + section.frame.forward[0] * along), quantize(eye[1] + range(rng, 2, 7)), quantize(eye[2] + section.frame.right[2] * lateral + section.frame.forward[2] * along)],
        right: [...section.frame.forward], travel: quantize(range(rng, 1.2, 2.5)), bob: quantize(range(rng, .5, 1.2)), period: quantize(range(rng, 5, 8)), phase: quantize(range(rng, 0, 5)), radius: 1.05, plannedRefill: plannedSectionIds.has(section.id) };
      const reach = target.radius + pmath.hypot(target.travel, target.bob);
      if (course.boxes.some(box => pointBoxClearance(box, target.center) < reach + 2)) continue;
      if (course.sections.some(other => {
        const a = other.frame.origin, b = other.landing, dx = b[0] - a[0], dz = b[2] - a[2], span = dx * dx + dz * dz;
        const t = Math.max(0, Math.min(1, ((target.center[0] - a[0]) * dx + (target.center[2] - a[2]) * dz) / Math.max(.01, span)));
        const alongY = a[1] + (b[1] - a[1]) * t;
        return Math.abs(target.center[1] - alongY) < 14 && pmath.hypot(target.center[0] - a[0] - dx * t, target.center[2] - a[2] - dz * t) < reach + 7;
      })) continue;
      const fixture = { ...course, targets: [target] };
      let visible = true;
      for (let sample = 0; sample < 12; sample++) {
        const time = sample * target.period / 12, position = sampleRaceTarget(target, time);
        if (eyes.some(from => !raycastRaceTarget(fixture, from, [position[0] - from[0], position[1] - from[1], position[2] - from[2]], time))) { visible = false; break; }
      }
      if (!visible) continue;
      targets.push(target); break;
    }
  }
  return targets;
}

/** Static and moving cover for the common projectile solver. */
export function raceShotSolid(course:Pick<RaceCourse,'radius'|'boxes'|'shutters'|'arena'|'rhythm'> & {failY?:number},from:RaceVec3,to:RaceVec3,time:number):number|null {
  const t=staticField(course).segmentHit(from[0],from[1]+course.radius,from[2],to[0],to[1]+course.radius,to[2]);
  let best=t>=0?t:null;
  for(const panel of [...course.shutters.flatMap(shutter=>raceShutterPose(shutter,time).panels),...raceArenaWalls(course,time)]){
    const hit=segmentBox(panel,from,to);if(hit!==null)best=best===null?hit:Math.min(best,hit);
  }
  const floor=(course.failY??-1000)-60;
  if(to[1]<floor){const hit=from[1]<=floor?0:(floor-from[1])/(to[1]-from[1]);best=best===null?hit:Math.min(best,hit);}
  return best;
}
